// Step 19 — generic finding card used across the intelligence
// workspace's attention queue, risk list and buyer/contract findings.
//
// The card visually distinguishes:
//   - the finding itself (AI/system-generated observation),
//   - its underlying evidence (provenance chain from the raw row),
//   - and human-controlled actions ("Fråga Qada" launches the
//     existing chat; no autonomous execution ever happens here).

import { useState } from "react";
import { Link } from "react-router-dom";

import AIAskQadaButton from "./AIAskQadaButton";
import AIEvidencePanel from "./AIEvidencePanel";
import { KIND, severityLabel } from "./helpers";

const KIND_LABEL = {
  [KIND.RISK]: "Risk",
  [KIND.PREPAREDNESS]: "Beredskapsgap",
  [KIND.BUYER_SIGNAL]: "Köparsignal",
  [KIND.CONTRACT_UNCERTAINTY]: "Kontrakts-osäkerhet",
  [KIND.CONTRACT_REVIEW]: "Kontrakt för granskning",
  [KIND.ACTION_PENDING]: "Åtgärd väntar",
  [KIND.SUGGESTED_ACTION]: "Föreslagen åtgärd",
};

function _originLabel(finding) {
  // fact_or_inference is populated by the backend on preparedness /
  // buyer-intel observations. Only render the label when it's actually
  // present — we never invent an origin.
  if (finding.fact_or_inference === "fact") return "Fakta";
  if (finding.fact_or_inference === "inference") return "Slutsats";
  // Actions/contracts stemming from human-controlled workflow rows —
  // the human still approves, so we tag them "System" rather than
  // "AI"-writing anything.
  if (finding.kind === KIND.ACTION_PENDING) return "Human review";
  if (finding.kind === KIND.CONTRACT_REVIEW) return "Human review";
  return "AI-observation";
}

export default function AIFindingCard({ finding, workspaceId }) {
  const [showEvidence, setShowEvidence] = useState(false);

  const kindLabel = KIND_LABEL[finding.kind] || "Observation";
  const origin = _originLabel(finding);
  const severity = finding.severity;

  // Only build a link when the finding targets a domain object the
  // AI workspace already knows how to reach.
  let followLink = null;
  if (finding.acquirer_id && workspaceId) {
    followLink = {
      to: `/deals/${workspaceId}/buyers/${finding.acquirer_id}`,
      label: "Öppna köpare →",
    };
  } else if (finding.contract_id && workspaceId) {
    followLink = {
      to: `/deals/${workspaceId}/contracts`,
      label: "Öppna avtal →",
    };
  } else if (finding.action_id && workspaceId) {
    followLink = {
      to: `/deals/${workspaceId}/actions`,
      label: "Öppna åtgärd →",
    };
  }

  return (
    <article
      className="qai-finding-card"
      data-kind={finding.kind}
      data-severity={severity}
    >
      <header className="qai-finding-head">
        <div className="qai-finding-badges">
          <span className="qai-finding-badge" data-tone="kind">
            {kindLabel}
          </span>
          <span className="qai-finding-badge" data-tone="origin">
            {origin}
          </span>
          <span
            className="qai-finding-badge qai-finding-severity"
            data-severity={severity}
          >
            {severityLabel(severity)}
          </span>
        </div>
        {finding.user_action_required && (
          <span className="qai-finding-flag">Kräver mänskligt beslut</span>
        )}
      </header>

      <h3 className="qai-finding-title">{finding.title}</h3>
      {finding.summary && (
        <p className="qai-finding-summary">{finding.summary}</p>
      )}

      <div className="qai-finding-actions">
        {finding.ask_prompt && (
          <AIAskQadaButton
            prompt={finding.ask_prompt}
            workspaceId={workspaceId}
            label="Fråga Qada"
          />
        )}
        {followLink && (
          <Link to={followLink.to} className="qai-finding-link">
            {followLink.label}
          </Link>
        )}
        <button
          type="button"
          className="qai-finding-evidence-toggle"
          onClick={() => setShowEvidence((s) => !s)}
          aria-expanded={showEvidence}
        >
          {showEvidence ? "Dölj underlag" : "Visa underlag"}
        </button>
      </div>

      {showEvidence && <AIEvidencePanel finding={finding} />}
    </article>
  );
}
